import { createUseFetch } from "hooks/use-fetch";
import React from "react";
import { symmetricDifference } from "ramda";
import { v4 as uuidv4 } from "uuid";
import { AxiosInstance } from "axios";

export type ColumnDetail<T = any> = {
  bFilterable: boolean;
  bLocked: boolean;
  bSortable: boolean;
  bVisible: boolean;
  columnKey: keyof T;
  filterMetadata: {
    filterType: string;
    details: null | any;
  };
  labelKey: string;
  labelValue: string;
  orderNumber: number;
  tooltip: null | string;
  type: string;
};

type TableData<T = any> = {
  id: null | string | number;
  status: string;
  errors: any[];
  details: {
    listData: T[];
    filters: string;
    sortBy: string;
    errors: any[];
    columns: ColumnDetail<T>[];
    metadata: any;
    pageNumber: number;
    pageSize: number;
    resultsCount: number;
    searchString: null | string;
    summaryRows: any[];
  };
};

type ColumnSortData<T> = {
  [K in keyof T]?: "ASC" | "DESC";
};

export type TableOptions = {
  url: string;
  sortBy?: string;
  filters?: string;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  bClearFilters?: boolean;
  bClearSortBy?: boolean;
  bClearSearch?: boolean;
};

function serialiseSortData<T>(data: ColumnSortData<T>): string {
  const keys = Object.keys(data);
  const values = keys.map(k => `${k} ${data[k as keyof T]}`);
  return values.join(" | ");
}

function deserialiseSortData<T>(sortString: string): ColumnSortData<T> {
  try {
    const pairs = sortString.split(" | ");
    const keyValues = pairs.map(pair => {
      const kv = pair.split(" ");
      if (kv.length !== 2) throw new Error(`Bad sort string: ${pair}`);
      return kv as [keyof T, "ASC" | "DESC"];
    });
    let data: ColumnSortData<T> = {};
    keyValues.forEach(([k, v]) => {
      data[k] = v;
    });
    return data;
  } catch (error) {
    return {};
  }
}

type State = Omit<TableOptions, "url"> & { hasFetchedOnce?: boolean };

type Action =
  | { type: "set_searchstring"; payload: string; resetSearch: boolean }
  | { type: "set_sortstring"; payload: string; resetSort: boolean }
  | {
      type: "set_filterstring";
      payload: string;
      resetFilter: boolean;
    }
  | {
      type: "change_pagesize";
      payload: number;
    }
  | {
      type: "next_page";
      maxPages: number;
    }
  | {
      type: "prev_page";
    }
  | { type: "goto_page"; payload: number; maxPages: number }
  | {
      type: "data_fetched";
      payload: State;
    };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "set_searchstring":
      return {
        ...state,
        search: action.payload,
        bClearSearch: action.resetSearch,
      };
    case "set_sortstring":
      return {
        ...state,
        sortBy: action.payload,
        bClearSortBy: action.resetSort,
      };
    case "set_filterstring":
      return {
        ...state,
        filters: action.payload,
        bClearFilters: action.resetFilter,
      };
    case "change_pagesize":
      return {
        ...state,
        pageSize: action.payload,
      };
    case "next_page": {
      const nextPage = state.pageNumber ? state.pageNumber + 1 : 1;
      return {
        ...state,
        pageNumber: action.maxPages >= nextPage ? nextPage : state.pageNumber,
      };
    }
    case "prev_page": {
      const prevPage = state.pageNumber ? state.pageNumber - 1 : 1;
      return { ...state, pageNumber: prevPage > 0 ? prevPage : 1 };
    }
    case "goto_page": {
      if (action.payload > 0 && action.payload <= action.maxPages) {
        return { ...state, pageNumber: action.payload };
      }
      return state;
    }
    case "data_fetched":
      return { ...action.payload, hasFetchedOnce: true };
  }
}

export function createUseTable(axiosInstance: AxiosInstance) {
  const useFetch = createUseFetch(axiosInstance);
  return function useTable<T, E>(options: TableOptions) {
    type S = State;
    type A = Action;
    const { url, ...initialState } = options;

    const [store, dispatch] = React.useReducer<React.Reducer<S, A>>(
      reducer,
      initialState
    );

    const {
      sortBy,
      filters,
      search,
      bClearFilters,
      bClearSortBy,
      bClearSearch,
      pageSize,
      pageNumber,
      hasFetchedOnce,
    } = store;

    const config = React.useMemo(() => {
      const filterString = filters === "reset" ? "" : filters;
      const clearFilters = filters === "reset" ? true : bClearFilters;
      return {
        params: {
          sortBy,
          filters: filterString || undefined,
          search,
          bClearFilters: clearFilters,
          bClearSearch,
          bClearSortBy,
          pageSize,
          pageNumber,
          uuid: hasFetchedOnce ? undefined : uuidv4(),
        },
      };
    }, [
      sortBy,
      pageSize,
      pageNumber,
      filters,
      search,
      bClearFilters,
      bClearSortBy,
      bClearSearch,
      hasFetchedOnce,
    ]);

    const { loading, error, data, reload } = useFetch<TableData<T>, E>(
      url,
      config
    );

    // I think this is a valid use of eslint-disable-next-line. Any change to store will
    // trigger a change to data, after it has fetched new data, which is when we want to
    // do the check. If store is in the dependency array, the check will also run whenever
    // store changes, at which point it is out of sync with the up-to-date data that will come.
    React.useEffect(() => {
      if (data) {
        if (
          data.details.filters !== store.filters ||
          data.details.sortBy !== store.sortBy ||
          data.details.pageSize !== store.pageSize
        ) {
          dispatch({ type: "data_fetched", payload: data.details });
        }
      }
      // eslint-disable-next-line
    }, [data]);

    const errors = data?.errors ?? [];
    if (errors.length > 0) throw new Error("Error building table");

    const initialSortBy = data?.details.sortBy;

    const deserialisedSort = React.useMemo(
      () => (initialSortBy ? deserialiseSortData<any>(initialSortBy) : {}),
      [initialSortBy]
    );

    const toggleColumnSort = React.useCallback(
      (key: keyof T) => {
        const currentSort = deserialisedSort[key];
        const nextSort =
          currentSort === "DESC"
            ? "ASC"
            : currentSort === "ASC"
            ? "RESET"
            : "DESC";
        const newSort: any = { ...deserialisedSort };
        if (nextSort === "RESET") {
          delete newSort[key];
        } else {
          newSort[key] = nextSort;
        }
        const sortString = serialiseSortData(newSort);
        dispatch({
          type: "set_sortstring",
          payload: sortString,
          resetSort: sortString === "",
        });
      },
      [dispatch, deserialisedSort]
    );

    const setFilters = React.useCallback((newFilters: string) => {
      dispatch({
        type: "set_filterstring",
        payload: newFilters,
        resetFilter: newFilters.length === 0,
      });
    }, []);

    const changeSearch = React.useCallback((searchString: string) => {
      dispatch({
        type: "set_searchstring",
        payload: searchString,
        resetSearch: searchString === "",
      });
    }, []);

    // Get the last page.
    const lastPage = Math.ceil(
      data ? data.details.resultsCount / data.details.pageSize : 0
    );

    const lastOnPage = data
      ? data.details.pageSize * data.details.pageNumber
      : 0;
    const firstOnPage = data ? lastOnPage - data.details.pageSize + 1 : 0;
    // Display the smallest of the total results count, and the calculated number.
    // This avoids labels with "Showing 11-25 of 18 results" etc.
    const lastOnPageDisplay = data
      ? Math.min(lastOnPage, data.details.resultsCount)
      : 0;

    const changePageSize = React.useCallback(
      (payload: number) => {
        dispatch({ type: "change_pagesize", payload });
      },
      [dispatch]
    );

    const goToNextPage = React.useCallback(() => {
      dispatch({ type: "next_page", maxPages: lastPage });
    }, [dispatch, lastPage]);

    const goToPrevPage = React.useCallback(() => {
      dispatch({ type: "prev_page" });
    }, [dispatch]);

    const goToPage = React.useCallback(
      (page: number) => {
        dispatch({ type: "goto_page", payload: page, maxPages: lastPage });
      },
      [dispatch, lastPage]
    );

    const hasNextPage = data ? data.details.pageNumber < lastPage : false;
    const hasPrevPage = data ? data.details.pageNumber > 1 : false;

    const formattedData = React.useMemo(
      () =>
        data == null
          ? undefined
          : {
              ...data,
              details: {
                ...data.details,
                sortBy: deserialisedSort,
                filters,
                lastPage,
                firstOnPage,
                lastOnPage: lastOnPageDisplay,
                hasNextPage,
                hasPrevPage,
              },
              actions: {
                toggleColumnSort,
                setFilters,
                changeSearch,
                changePageSize,
                goToNextPage,
                goToPrevPage,
                goToPage,
              },
            },
      [
        data,
        setFilters,
        changeSearch,
        deserialisedSort,
        filters,
        firstOnPage,
        goToNextPage,
        goToPrevPage,
        goToPage,
        hasNextPage,
        hasPrevPage,
        lastOnPageDisplay,
        lastPage,
        toggleColumnSort,
        changePageSize,
      ]
    );

    return {
      loading,
      error,
      data: formattedData,
      reload,
    };
  };
}

export function useTableSelection<T>(
  idColumn: keyof T,
  selectBox?: (
    select: () => void,
    selected: boolean,
    id: number | string
  ) => React.ReactNode
) {
  const [selectedIDs, setSelected] = React.useState<(string | number)[]>([]);

  const rowSelection = React.useMemo(() => {
    const selectRow = (id: string | number) => {
      setSelected(prev =>
        selectedIDs.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
    };

    const selectAll = (ids: (string | number)[]) =>
      setSelected(prev =>
        symmetricDifference(ids, prev).length === 0 ? [] : ids
      );

    return {
      selectedIDs,
      selectRow,
      selectAll,
      idColumn,
      checkbox: selectBox,
    };
  }, [selectedIDs, setSelected, idColumn, selectBox]);

  return rowSelection;
}
